import * as React from 'react';

import { dataSourceIdsSelector } from 'src/selectors/datasourceIdsSelector';
import { ProjectFileUploadOptions } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import {
  INVALID_NAME_MATCHER,
  NAME_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidNameChars';

export const useFindDataSource = () => {
  const dataSourceNames = useReduxSelector(dataSourceIdsSelector);
  return React.useCallback(
    (dataSourceName: string) =>
      dataSourceNames.find((item) => item === dataSourceName),
    [dataSourceNames]
  );
};

export const useValidateDataSourceName = () => {
  const findDataSourceNames = useFindDataSource();
  return React.useCallback(
    (name: string): string => {
      if (!name) {
        return copy.components.projectResourceDialog
          .projectDataSourceCreationPanel.name.required;
      }

      if (!name.match(NAME_STARTS_WITH_MATCHER)) {
        return copy.components.projectResourceDialog
          .projectDataSourceCreationPanel.name.startsWith;
      }
      const invalidChars = name.match(INVALID_NAME_MATCHER);
      if (invalidChars) {
        return copy.components.projectResourceDialog.projectDataSourceCreationPanel.name.characters(
          invalidChars
        );
      }

      if (findDataSourceNames(name)) {
        return copy.components.projectResourceDialog
          .projectDataSourceCreationPanel.name.unique;
      }

      return '';
    },
    [findDataSourceNames]
  );
};

export const useAutomaticallySetHeadersOption = ({
  files,
  fileOptions,
  setFileOptions,
}: {
  files: Array<File | string>;
  fileOptions: ProjectFileUploadOptions;
  setFileOptions: (options: ProjectFileUploadOptions) => void;
}) => {
  const [isFileWithDuplicateHeaders, setIsFileWithDuplicateHeaders] =
    React.useState(false);

  const didFilesChange = useDidValueChange(files);
  const didFileOptionsChange = useDidValueChange(fileOptions);
  React.useEffect(() => {
    if (!files.length) {
      return;
    }

    if (didFilesChange || didFileOptionsChange) {
      const doFilesHaveDuplicateHeadersPromises = files.map(
        doesFileHaveDuplicateHeaders(fileOptions)
      );
      Promise.all(doFilesHaveDuplicateHeadersPromises).then((results) => {
        const doesAtLeastOneFileHaveDupeHeaders = results.some(
          (result) => result
        );
        setIsFileWithDuplicateHeaders(doesAtLeastOneFileHaveDupeHeaders);
      });
    }
  }, [didFileOptionsChange, fileOptions, files, didFilesChange]);
  const didHasDuplicateHeadersChange = useDidValueChange(
    isFileWithDuplicateHeaders
  );
  React.useEffect(() => {
    if (didHasDuplicateHeadersChange) {
      setFileOptions({
        ...fileOptions,
        header: isFileWithDuplicateHeaders ? false : fileOptions.header,
      });
    }
  }, [
    didHasDuplicateHeadersChange,
    fileOptions,
    isFileWithDuplicateHeaders,
    setFileOptions,
  ]);

  return { isThereDuplicateHeaders: isFileWithDuplicateHeaders };
};
